import {
  PERMISOS,
  PERMISOS_POR_ROL,
  formatearMoneda,
  type Permiso,
  type Rol,
  type UsuarioVista,
} from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, MoreHorizontal, Pencil, Power, ShieldCheck, UserPlus, UsersRound } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { api, ErrorApi, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Select } from '@/components/ui/form';
import { Dialogo, Menu, MenuContenido, MenuDisparador, MenuItem, MenuSeparador } from '@/components/ui/overlay';
import { fecha } from '@/lib/utils';
import { avisarError } from '@/lib/avisos';

const ROLES: { valor: Rol; etiqueta: string }[] = [
  { valor: 'BARBERO', etiqueta: 'Barbero' },
  { valor: 'RECEPCION', etiqueta: 'Recepción' },
  { valor: 'DUENO', etiqueta: 'Dueño' },
];
const etiquetaRol = (rol: Rol) => ROLES.find((r) => r.valor === rol)?.etiqueta ?? rol;

type Dialogos =
  | { tipo: 'crear' }
  | { tipo: 'editar'; usuario: UsuarioVista }
  | { tipo: 'permisos'; usuario: UsuarioVista }
  | { tipo: 'password'; usuario: UsuarioVista }
  | null;

function useInvalidarUsuarios() {
  const qc = useQueryClient();
  return () => {
    // Los permisos cambian quién aparece en la agenda y en los selectores de barbero.
    void qc.invalidateQueries({ queryKey: ['barberos'] });
    void qc.invalidateQueries({ queryKey: ['agenda'] });
    return qc.invalidateQueries({ queryKey: ['usuarios'] });
  };
}

function erroresCampo(error: unknown): Record<string, string> {
  if (!(error instanceof ErrorApi) || !Array.isArray(error.details)) return {};
  return Object.fromEntries(
    (error.details as { path?: (string | number)[]; message: string }[]).map((d) => [String(d.path?.[0] ?? ''), d.message]),
  );
}

function DialogoUsuario({ usuario, onCerrar }: { usuario?: UsuarioVista; onCerrar: () => void }) {
  const { usuario: actor } = useSesion();
  const invalidar = useInvalidarUsuarios();
  const [datos, setDatos] = useState({
    nombre: usuario?.nombre ?? '',
    usuario: usuario?.usuario ?? '',
    password: '',
    rol: (usuario?.rol ?? 'BARBERO') as Rol,
    telefono: usuario?.telefono ?? '',
    email: usuario?.email ?? '',
    sueldo: usuario?.sueldoSemanal != null ? String(usuario.sueldoSemanal / 100) : '',
  });

  const guardar = useMutation({
    mutationFn: () => {
      const comunes = {
        nombre: datos.nombre,
        rol: datos.rol,
        telefono: datos.telefono || undefined,
        email: datos.email || undefined,
        sueldoSemanal: datos.sueldo ? Math.round(Number.parseFloat(datos.sueldo) * 100) : null,
      };
      return usuario
        ? api(`/usuarios/${usuario.id}`, { method: 'PATCH', body: comunes })
        : api('/usuarios', { method: 'POST', body: { ...comunes, usuario: datos.usuario, password: datos.password } });
    },
    onSuccess: () => {
      toast.success(usuario ? 'Usuario actualizado' : 'Usuario creado');
      void invalidar();
      onCerrar();
    },
  });
  const errores = erroresCampo(guardar.error);
  const set = (campo: keyof typeof datos) => (e: { target: { value: string } }) => setDatos((d) => ({ ...d, [campo]: e.target.value }));

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    guardar.mutate();
  };

  return (
    <Dialogo
      abierto
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={usuario ? `Editar a ${usuario.nombre}` : 'Nuevo usuario'}
      descripcion={usuario ? undefined : 'Cada persona tiene su propia cuenta. Queda registrado quién la creó.'}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button type="submit" form="form-usuario" cargando={guardar.isPending}>
            {usuario ? 'Guardar cambios' : 'Crear usuario'}
          </Button>
        </>
      }
    >
      <form id="form-usuario" onSubmit={enviar} className="grid gap-4 sm:grid-cols-2">
        <Campo etiqueta="Nombre completo" requerido error={errores.nombre} className="sm:col-span-2">
          {(p) => <Input {...p} value={datos.nombre} onChange={set('nombre')} required autoFocus />}
        </Campo>
        {!usuario && (
          <>
            <Campo etiqueta="Usuario" requerido error={errores.usuario} ayuda="Con el que inicia sesión (sin espacios).">
              {(p) => <Input {...p} value={datos.usuario} onChange={set('usuario')} autoCapitalize="none" required />}
            </Campo>
            <Campo etiqueta="Contraseña inicial" requerido error={errores.password} ayuda="Mínimo 8 caracteres, con letras y números.">
              {(p) => <Input {...p} type="password" value={datos.password} onChange={set('password')} autoComplete="new-password" required />}
            </Campo>
          </>
        )}
        <Campo etiqueta="Rol" requerido error={errores.rol}>
          {(p) => (
            <Select {...p} value={datos.rol} onChange={set('rol')}>
              {ROLES.filter((r) => r.valor !== 'DUENO' || actor?.rol === 'DUENO').map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.etiqueta}
                </option>
              ))}
            </Select>
          )}
        </Campo>
        <Campo etiqueta="Sueldo semanal (opcional)" error={errores.sueldoSemanal} ayuda="Déjalo vacío si solo gana comisiones.">
          {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={datos.sueldo} onChange={set('sueldo')} className="cifras" />}
        </Campo>
        <Campo etiqueta="Teléfono" error={errores.telefono}>
          {(p) => <Input {...p} type="tel" value={datos.telefono} onChange={set('telefono')} />}
        </Campo>
        <Campo etiqueta="Correo" error={errores.email}>
          {(p) => <Input {...p} type="email" value={datos.email} onChange={set('email')} />}
        </Campo>
        {guardar.error && !Object.keys(errores).length && (
          <p role="alert" className="text-[13px] text-destructive sm:col-span-2">
            {mensajeError(guardar.error)}
          </p>
        )}
      </form>
    </Dialogo>
  );
}

function DialogoPermisos({ usuario, onCerrar }: { usuario: UsuarioVista; onCerrar: () => void }) {
  const { usuario: actor } = useSesion();
  const invalidar = useInvalidarUsuarios();
  const base = useMemo(() => new Set<Permiso>(PERMISOS_POR_ROL[usuario.rol]), [usuario.rol]);
  const [activos, setActivos] = useState<Set<Permiso>>(() => {
    const s = new Set<Permiso>([...base, ...usuario.permisosExtra]);
    usuario.permisosRevocados.forEach((p) => s.delete(p));
    return s;
  });

  const guardar = useMutation({
    mutationFn: () =>
      api(`/usuarios/${usuario.id}/permisos`, {
        method: 'PATCH',
        body: {
          extras: [...activos].filter((p) => !base.has(p)),
          revocados: [...base].filter((p) => !activos.has(p)),
        },
      }),
    onSuccess: () => {
      toast.success('Permisos actualizados');
      void invalidar();
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });

  const grupos = useMemo(() => {
    const mapa = new Map<string, Permiso[]>();
    for (const [clave, def] of Object.entries(PERMISOS) as [Permiso, { grupo: string }][]) {
      mapa.set(def.grupo, [...(mapa.get(def.grupo) ?? []), clave]);
    }
    return [...mapa.entries()];
  }, []);

  const puedeTocar = (p: Permiso) => actor?.rol === 'DUENO' || actor?.permisos.includes(p);

  return (
    <Dialogo
      abierto
      ancho="lg"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={`Permisos de ${usuario.nombre}`}
      descripcion={`Rol base: ${etiquetaRol(usuario.rol)}. Marca lo que puede hacer además de su rol, o quita permisos del rol.`}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} cargando={guardar.isPending}>
            Guardar permisos
          </Button>
        </>
      }
    >
      <div className="grid gap-5 sm:grid-cols-2">
        {grupos.map(([grupo, permisos]) => (
          <fieldset key={grupo}>
            <legend className="mb-1.5 font-display text-[15px]">{grupo}</legend>
            <div className="flex flex-col gap-1">
              {permisos.map((p) => (
                <label key={p} className="flex items-start gap-2.5 rounded-md px-2 py-1.5 text-[13px] hover:bg-muted has-[:disabled]:opacity-50">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-[var(--bb-accent)]"
                    checked={activos.has(p)}
                    disabled={!puedeTocar(p)}
                    onChange={(e) =>
                      setActivos((prev) => {
                        const s = new Set(prev);
                        if (e.target.checked) s.add(p);
                        else s.delete(p);
                        return s;
                      })
                    }
                  />
                  <span>
                    {PERMISOS[p].titulo}
                    {base.has(p) && <span className="ml-1.5 text-[11px] text-foreground-muted">(del rol)</span>}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>
    </Dialogo>
  );
}

function DialogoPassword({ usuario, onCerrar }: { usuario: UsuarioVista; onCerrar: () => void }) {
  const [password, setPassword] = useState('');
  const guardar = useMutation({
    mutationFn: () => api(`/usuarios/${usuario.id}/restablecer-password`, { method: 'POST', body: { password } }),
    onSuccess: () => {
      toast.success('Contraseña restablecida. Se cerraron sus sesiones abiertas.');
      onCerrar();
    },
  });
  const errores = erroresCampo(guardar.error);
  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo="Restablecer contraseña"
      descripcion={`Nueva contraseña para ${usuario.nombre}.`}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button type="submit" form="form-pass" cargando={guardar.isPending} disabled={!password}>
            Restablecer
          </Button>
        </>
      }
    >
      <form
        id="form-pass"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate();
        }}
      >
        <Campo etiqueta="Nueva contraseña" requerido error={errores.password ?? (guardar.error ? mensajeError(guardar.error) : undefined)} ayuda="Mínimo 8 caracteres, con letras y números.">
          {(p) => <Input {...p} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" autoFocus />}
        </Campo>
      </form>
    </Dialogo>
  );
}

export function UsuariosPage() {
  const invalidar = useInvalidarUsuarios();
  const [dialogo, setDialogo] = useState<Dialogos>(null);
  const consulta = useQuery({ queryKey: ['usuarios'], queryFn: () => api<UsuarioVista[]>('/usuarios') });

  const cambiarActivo = useMutation({
    mutationFn: (u: UsuarioVista) => api(`/usuarios/${u.id}/activo`, { method: 'PATCH', body: { activo: !u.activo } }),
    onSuccess: (_, u) => {
      toast.success(u.activo ? `${u.nombre} fue dado de baja` : `${u.nombre} fue reactivado`);
      void invalidar();
    },
    onError: (e) => avisarError(e),
  });

  return (
    <ModuleLayout titulo="Configuración" secciones={SECCIONES_POR_MODULO.configuracion}>
      <EncabezadoPagina
        titulo="Usuarios y permisos"
        descripcion="Alta y baja de cuentas, permisos individuales y recuperación de accesos."
        acciones={
          <Button onClick={() => setDialogo({ tipo: 'crear' })}>
            <UserPlus />
            Nuevo usuario
          </Button>
        }
      />
      <Tarjeta>
        {consulta.isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Esqueleto key={i} className="h-10" />
            ))}
          </div>
        ) : !consulta.data?.length ? (
          <Vacio icono={<UsersRound />} titulo="Sin usuarios" />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Usuario</Th>
                <Th>Rol</Th>
                <Th className="text-right">Sueldo semanal</Th>
                <Th>Registrado por</Th>
                <Th>Estado</Th>
                <Th className="w-12">
                  <span className="sr-only">Acciones</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {consulta.data.map((u) => (
                <Tr key={u.id} className={u.activo ? '' : 'opacity-60'}>
                  <Td className="font-medium">{u.nombre}</Td>
                  <Td className="text-foreground-muted">{u.usuario}</Td>
                  <Td>
                    <Insignia tono={u.rol === 'DUENO' ? 'solido' : u.rol === 'RECEPCION' ? 'info' : 'acento'}>{etiquetaRol(u.rol)}</Insignia>
                    {u.permisosExtra.length > 0 && (
                      <span className="ml-1.5 text-[11.5px] text-foreground-muted">+{u.permisosExtra.length} permisos</span>
                    )}
                  </Td>
                  <Td className="cifras text-right">{u.sueldoSemanal != null ? formatearMoneda(u.sueldoSemanal) : '—'}</Td>
                  <Td className="text-[12.5px] text-foreground-muted">
                    {u.registradoPor?.nombre ?? 'Sistema'} · {fecha(u.createdAt)}
                  </Td>
                  <Td>
                    <Insignia tono={u.activo ? 'exito' : 'neutro'}>{u.activo ? 'Activo' : 'Dado de baja'}</Insignia>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuDisparador asChild>
                        <Button variante="fantasma" tamano="icono" aria-label={`Acciones para ${u.nombre}`}>
                          <MoreHorizontal />
                        </Button>
                      </MenuDisparador>
                      <MenuContenido align="end">
                        <MenuItem onSelect={() => setDialogo({ tipo: 'editar', usuario: u })}>
                          <Pencil /> Editar datos
                        </MenuItem>
                        <MenuItem onSelect={() => setDialogo({ tipo: 'permisos', usuario: u })}>
                          <ShieldCheck /> Permisos
                        </MenuItem>
                        <MenuItem onSelect={() => setDialogo({ tipo: 'password', usuario: u })}>
                          <KeyRound /> Restablecer contraseña
                        </MenuItem>
                        <MenuSeparador />
                        <MenuItem onSelect={() => cambiarActivo.mutate(u)} className={u.activo ? 'text-destructive' : ''}>
                          <Power /> {u.activo ? 'Dar de baja' : 'Reactivar'}
                        </MenuItem>
                      </MenuContenido>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>

      {dialogo?.tipo === 'crear' && <DialogoUsuario onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'editar' && <DialogoUsuario usuario={dialogo.usuario} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'permisos' && <DialogoPermisos usuario={dialogo.usuario} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'password' && <DialogoPassword usuario={dialogo.usuario} onCerrar={() => setDialogo(null)} />}
    </ModuleLayout>
  );
}
